import { z } from "zod";

/**
 * Bump when the shape changes. Keep samples/ in step. Version 2 added the video block, version 3 the timeline
 * block. Each older version is a valid newer deck, so older specs (stored versions, exported files) are read as 3.
 */
export const SCHEMA_VERSION = 3 as const;

const Id = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, "ids are lowercase kebab-case");

/* ---------- references ---------- */

export const Reference = z.object({
  id: Id,
  title: z.string().min(1),
  authors: z.string().optional(), // "Yao et al." — never invented by the model
  venue: z.string().optional(),   // "ICLR"
  year: z.number().int().min(1900).max(2100).optional(),
  url: z.string().url(),
});
export type Reference = z.infer<typeof Reference>;

const RefIds = z.array(Id).max(6).default([]);

/* ---------- flow ---------- */

export const FlowEdge = z.object({
  from: Id,
  to: Id,
  label: z.string().max(40).optional(),
  kind: z.enum(["forward", "loop"]).default("forward"), // loop = dashed, routed below
});
export type FlowEdge = z.infer<typeof FlowEdge>;

/** A small flow inside a zoomed node. One level of nesting only. */
export const SubFlow = z.object({
  nodes: z
    .array(z.object({ id: Id, label: z.string().max(40), caption: z.string().max(60).optional() }))
    .min(2)
    .max(5),
  edges: z.array(FlowEdge).max(8),
  emphasis: Id.optional(), // node drawn yellow
});

export const NodeDetail = z.object({
  title: z.string().max(80),
  lead: z.string().max(240).optional(),
  flow: SubFlow.optional(),
  code: z.string().max(300).optional(), // one-line example, rendered in the dark code bar
  refs: RefIds,
});

export const FlowNode = z.object({
  id: Id,
  label: z.string().max(24),
  caption: z.string().max(48).optional(),
  detail: NodeDetail.optional(), // present = this node is a zoom stop
});
export type FlowNode = z.infer<typeof FlowNode>;

/* ---------- blocks ---------- */

const BlockBase = z.object({
  id: Id,
  notes: z.string().max(2000).optional(), // speaker notes, never shown to the audience
});

export const ConceptBlock = BlockBase.extend({
  type: z.literal("concept"),
  title: z.string().max(80),
  lead: z.string().max(240).optional(),
  bullets: z.array(z.string().max(120)).max(4).default([]),
  statement: z.string().max(140).optional(), // one big line instead of bullets
  refs: RefIds,
});

export const FlowBlock = BlockBase.extend({
  type: z.literal("flow"),
  title: z.string().max(80),
  lead: z.string().max(240).optional(),
  nodes: z.array(FlowNode).min(2).max(7),
  edges: z.array(FlowEdge).max(12),
  loopLabel: z.string().max(40).optional(),
  refs: RefIds,
});

const PollOption = z.object({ id: Id, label: z.string().max(80) });

export const PollBlock = BlockBase.extend({
  type: z.literal("poll"),
  question: z.string().max(140),
  kind: z.enum(["single", "multi", "scale", "wordcloud", "open"]),
  options: z.array(PollOption).max(6).default([]), // single | multi
  scale: z
    .object({
      min: z.number().int().default(1),
      max: z.number().int().default(5),
      minLabel: z.string().max(30).optional(),
      maxLabel: z.string().max(30).optional(),
    })
    .optional(), // scale
});

export const QuizBlock = BlockBase.extend({
  type: z.literal("quiz"),
  question: z.string().max(140),
  options: z.array(PollOption).min(2).max(6),
  correctOptionId: Id,
  explanation: z.string().max(240).optional(),
});

export const ScreenshotPin = z.object({
  id: Id,
  x: z.number().min(0).max(1), // fraction of image width
  y: z.number().min(0).max(1), // fraction of image height
  question: z.string().max(140),
  zoom: z.number().min(1).max(4).default(1.9),
});

export const ScreenshotBlock = BlockBase.extend({
  type: z.literal("screenshot"),
  title: z.string().max(80),
  assetId: z.string().min(1).nullable(), // null until the presenter uploads the image
  filename: z.string().max(120),
  caption: z.string().max(200).optional(),
  pins: z.array(ScreenshotPin).max(9).default([]),
  refs: RefIds,
});

/** A clip (MP4, WebM or GIF) played on the projector. Phones show its title only. */
export const VideoBlock = BlockBase.extend({
  type: z.literal("video"),
  title: z.string().max(80),
  lead: z.string().max(240).optional(),
  assetId: z.string().min(1).nullable(), // null until the presenter uploads the clip
  filename: z.string().max(120),
  caption: z.string().max(200).optional(),
  loop: z.boolean().default(true),
  muted: z.boolean().default(true),
  start: z.number().min(0).max(36000).optional(), // seconds: play from here
  end: z.number().min(0).max(36000).optional(), // seconds: stop (or loop back) here
  refs: RefIds,
});

/** One point on a timeline: when, what, and a line on what changed. */
export const TimelineItem = z.object({
  id: Id,
  when: z.string().min(1).max(16), // "2020", "2023–24", "Jun 2025"
  label: z.string().max(40),
  caption: z.string().max(140).optional(),
  refs: RefIds,
});
export type TimelineItem = z.infer<typeof TimelineItem>;

/**
 * Points in time on one line, oldest first. With reveal "steps" (the default) each Next shows the next point, the
 * newest one highlighted; with "all" they show together and `emphasis` picks the highlighted one.
 */
export const TimelineBlock = BlockBase.extend({
  type: z.literal("timeline"),
  title: z.string().max(80),
  lead: z.string().max(240).optional(),
  items: z.array(TimelineItem).min(2).max(6),
  reveal: z.enum(["steps", "all"]).default("steps"),
  emphasis: Id.optional(),
  refs: RefIds,
});

export const ReferencesBlock = BlockBase.extend({
  type: z.literal("references"),
  title: z.string().max(80).default("Read more"),
  referenceIds: z.array(Id).optional(), // omitted = all deck references
});

export const Block = z.discriminatedUnion("type", [
  ConceptBlock,
  FlowBlock,
  PollBlock,
  QuizBlock,
  ScreenshotBlock,
  VideoBlock,
  TimelineBlock,
  ReferencesBlock,
]);
export type Block = z.infer<typeof Block>;
export type BlockType = Block["type"];

/* ---------- deck ---------- */

function dupes(ids: string[]): string[] {
  const seen = new Set<string>();
  return [...new Set(ids.filter((id) => seen.has(id) || !seen.add(id)))];
}

export const Section = z.object({
  id: Id,
  title: z.string().max(80),
  minutes: z.number().int().min(1).max(120),
  ahead: z.array(z.string().max(60)).max(4).default([]), // "what's ahead" list on the section slide
  blocks: z.array(Block).min(1).max(12),
});
export type Section = z.infer<typeof Section>;

export const Deck = z
  .object({
    schemaVersion: z.union([z.literal(1), z.literal(2), z.literal(SCHEMA_VERSION)]).transform(() => SCHEMA_VERSION),
    title: z.string().max(80),
    subtitle: z.string().max(160).optional(),
    presenter: z.string().max(60),
    event: z.string().max(60).optional(), // "Knowledge sharing"
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    references: z.array(Reference).default([]),
    sections: z.array(Section).min(1).max(20),
  })
  .superRefine((deck, ctx) => {
    const refIds = new Set(deck.references.map((r) => r.id));
    dupes(deck.references.map((r) => r.id)).forEach((id) =>
      ctx.addIssue({ code: "custom", path: ["references"], message: `duplicate reference id ${id}` }),
    );
    const blockIds = new Set<string>();
    deck.sections.forEach((s, si) =>
      s.blocks.forEach((b, bi) => {
        const path = ["sections", si, "blocks", bi];
        if (blockIds.has(b.id)) ctx.addIssue({ code: "custom", path: [...path, "id"], message: `duplicate block id ${b.id}` });
        blockIds.add(b.id);
        const refs: string[] = [
          ...("refs" in b ? b.refs : []),
          ...(b.type === "references" ? b.referenceIds ?? [] : []),
          ...(b.type === "flow" ? b.nodes.flatMap((n) => n.detail?.refs ?? []) : []),
          ...(b.type === "timeline" ? b.items.flatMap((i) => i.refs) : []),
        ];
        refs.forEach((r) => {
          if (!refIds.has(r)) ctx.addIssue({ code: "custom", path, message: `unknown reference ${r}` });
        });
        if (b.type === "flow") {
          const ids = new Set(b.nodes.map((n) => n.id));
          dupes(b.nodes.map((n) => n.id)).forEach((id) =>
            ctx.addIssue({ code: "custom", path, message: `duplicate node id ${id}` }),
          );
          b.edges.forEach((e) => {
            if (!ids.has(e.from) || !ids.has(e.to))
              ctx.addIssue({ code: "custom", path, message: `edge ${e.from}->${e.to} points at a missing node` });
          });
          b.nodes.forEach((n) => {
            const sub = n.detail?.flow;
            if (!sub) return;
            const subIds = new Set(sub.nodes.map((s) => s.id));
            dupes(sub.nodes.map((s) => s.id)).forEach((id) =>
              ctx.addIssue({ code: "custom", path, message: `node ${n.id}: duplicate sub-flow node id ${id}` }),
            );
            sub.edges.forEach((e) => {
              if (!subIds.has(e.from) || !subIds.has(e.to))
                ctx.addIssue({
                  code: "custom",
                  path,
                  message: `node ${n.id}: sub-flow edge ${e.from}->${e.to} points at a missing node`,
                });
            });
            if (sub.emphasis && !subIds.has(sub.emphasis))
              ctx.addIssue({ code: "custom", path, message: `node ${n.id}: emphasis ${sub.emphasis} is not a sub-flow node` });
          });
        }
        if (b.type === "poll" || b.type === "quiz")
          dupes(b.options.map((o) => o.id)).forEach((id) =>
            ctx.addIssue({ code: "custom", path, message: `duplicate option id ${id}` }),
          );
        if (b.type === "quiz" && !b.options.some((o) => o.id === b.correctOptionId))
          ctx.addIssue({ code: "custom", path, message: "correctOptionId is not one of the options" });
        if (b.type === "poll" && (b.kind === "single" || b.kind === "multi") && b.options.length < 2)
          ctx.addIssue({ code: "custom", path, message: "choice polls need at least 2 options" });
        if (b.type === "poll" && b.scale && b.scale.min >= b.scale.max)
          ctx.addIssue({ code: "custom", path, message: "scale min must be less than max" });
        if (b.type === "screenshot")
          dupes(b.pins.map((p) => p.id)).forEach((id) =>
            ctx.addIssue({ code: "custom", path, message: `duplicate pin id ${id}` }),
          );
        if (b.type === "timeline") {
          dupes(b.items.map((i) => i.id)).forEach((id) =>
            ctx.addIssue({ code: "custom", path, message: `duplicate timeline item id ${id}` }),
          );
          if (b.emphasis && !b.items.some((i) => i.id === b.emphasis))
            ctx.addIssue({ code: "custom", path, message: `emphasis ${b.emphasis} is not one of the timeline items` });
        }
        if (b.type === "video" && b.start !== undefined && b.end !== undefined && b.end <= b.start)
          ctx.addIssue({ code: "custom", path, message: "video end must be after its start" });
      }),
    );
  });
export type Deck = z.infer<typeof Deck>;

/* ---------- slides (what the presenter navigates) ---------- */

export type Slide =
  | { kind: "title" }
  | { kind: "section"; sectionId: string; number: number }
  | { kind: "block"; sectionId: string; blockId: string; type: BlockType; stops: number };

/**
 * Stops: a flow with zooms = overview, one per node with detail, then the overview again (so a talk always
 * comes back out to the whole flow before moving on); screenshot = 1 + pins; a timeline revealed in steps = one per
 * item (stop k shows items 0..k); everything else 1.
 */
function flowStops(zooms: number): number {
  return zooms ? zooms + 2 : 1;
}

export function flattenDeck(deck: Deck): Slide[] {
  const slides: Slide[] = [{ kind: "title" }];
  deck.sections.forEach((s, i) => {
    slides.push({ kind: "section", sectionId: s.id, number: i + 1 });
    s.blocks.forEach((b) => {
      const stops =
        b.type === "flow" ? flowStops(b.nodes.filter((n) => n.detail).length)
        : b.type === "timeline" ? (b.reveal === "steps" ? b.items.length : 1)
        : b.type === "screenshot" ? 1 + b.pins.length
        : 1;
      slides.push({ kind: "block", sectionId: s.id, blockId: b.id, type: b.type, stops });
    });
  });
  return slides;
}

export type SlidePosition = { slideIndex: number; stop: number };

export * from "./titles.js";
export * from "./live.js";
export * from "./preflight.js";
export * from "./generation.js";
